// src/components/Upload/UploadComponent.tsx
import React, { useState, useCallback, useRef } from 'react';
import { useProcessing } from '../../context/ProcessingContext';



interface DemoMetadata {
  region: string;
  nearby_city: string;
  field_name: string;
  num_points: number;
  center: {
    lat: number;
    lon: number;
  };
}

export const UploadComponent: React.FC = () => {


  const { state, dispatch } = useProcessing();
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [demoMetadata, setDemoMetadata] = useState<DemoMetadata | null>(null);

  const handleUpload = async (file: File) => {
    setUploadError(null);
    if (!validateFile(file)) return;

    dispatch({ type: 'SET_ERROR', payload: null });
    dispatch({ type: 'SET_PROCESSING', payload: true });

    try {
      const formData = new FormData();
      formData.append('file', file);

      const result = await fetch('/api/upload', {
        method: 'POST',
        body: formData
      });

      const response = await result.json();
      
      if (response.error) {
        throw new Error(response.error);
      }

      dispatch({
        type: 'SET_RAW_DATA',
        payload: {
          data: response.data,
          plot: response.raw_plot,
          histogram: response.histogram,
          stats: response.stats,
          geographic_metadata: response.geographic_metadata
        }
      });

      setUploadComplete(true);
      dispatch({ type: 'SET_VIEW_STEP', payload: 'grid' });
      
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'Upload failed');
      dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Upload failed' });
    } finally {
      dispatch({ type: 'SET_PROCESSING', payload: false });
    }
  };

  const handleDemoClick = async () => {
    setDemoMetadata(null);
    dispatch({ type: 'SET_ERROR', payload: null });
    dispatch({ type: 'SET_PROCESSING', payload: true });

    try {
      const result = await fetch('/api/demo-data', {
        method: 'GET'
      });

      const response = await result.json();
    
      if (response.error) {
        throw new Error(response.error);
      }

      setDemoMetadata(response.demo_metadata);

      dispatch({
        type: 'SET_RAW_DATA',
        payload: {
          data: response.data,
          plot: response.raw_plot,
          histogram: response.histogram,
          stats: response.stats,
          geographic_metadata: response.geographic_metadata
        }
      });

      setUploadComplete(true);
      dispatch({ type: 'SET_VIEW_STEP', payload: 'grid' });
    
    } catch (error) {
      setUploadError('Failed to load demo data');
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load demo data' });
    } finally {
      dispatch({ type: 'SET_PROCESSING', payload: false });
    }
  };

  const validateFile = (file: File): boolean => {
    const validTypes = ['.csv', '.xlsx', '.xls', '.txt'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    
    if (!validTypes.includes(fileExtension)) {
      setUploadError('Please upload a CSV, Excel, or text file');
      return false;
    }

    const maxSize = 16 * 1024 * 1024; // 16MB
    if (file.size > maxSize) {
      setUploadError('File size must be less than 16MB');
      return false;
    }

    return true;
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      handleUpload(e.target.files[0]);
    }
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files?.[0]) {
      handleUpload(e.dataTransfer.files[0]);
    }
  }, []);

  if (uploadComplete && state.rawData) {
    return (
      <div className="panel-section">
        <div className="panel-content">
          <div className="panel-preview">
            {demoMetadata && (
              <div className="demo-metadata">
                <h2>{demoMetadata.field_name}</h2>
                <p className="location-info">
                  <span className="city">{demoMetadata.nearby_city}</span>
                  <span className="region">{demoMetadata.region}</span>
                  <span className="coordinates">
                    ({demoMetadata.center.lat.toFixed(3)}°, {demoMetadata.center.lon.toFixed(3)}°)
                  </span>
                </p>
                <p className="data-info">
                  Analyzing {demoMetadata.num_points} mysterious data points...
                </p>
              </div>
            )}
            
            <div className="data-visualization">
              <img 
                src={`data:image/png;base64,${state.rawData.plot}`}
                alt="Data visualization"
                className="preview-image"
              />
            </div>
            
            <div className="histogram-container">
              <h3>Value Distribution</h3>
              <img 
                src={`data:image/png;base64,${state.rawData.histogram}`}
                alt="Histogram"
                className="histogram-image"
              />
            </div>
            
            <div className="stats-display">
              <h3>Data Statistics</h3>
              <table>
                <tbody>
                  {Object.entries(state.rawData.stats).map(([key, value]) => (
                    <tr key={key}>
                      <th>{key.split('_').map(word => 
                        word.charAt(0).toUpperCase() + word.slice(1)
                      ).join(' ')}</th>
                      <td>{typeof value === 'number' ? value.toFixed(2) : value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="panel-section">
      <div className="panel-content">
        {/* Demo Section */}
        <div className="demo-section">
          <h3>From Scattered Points to Surface Maps in Seconds</h3>
          <div className="demo-preview">
            <div className="demo-images">
              <div className="demo-image-container">
                <img 
                  src="/static/demo_data/dataset/scatter.png"
                  alt="Sample data scatter plot"
                  className="demo-image"
                />
                <span className="image-caption">Input lat-long-z data</span>
              </div>
              <div className="demo-image-container">
                <img 
                  src="/static/demo_data/dataset/result.png"
                  alt="Sample interpolation result"
                  className="demo-image"
                />
                <span className="image-caption">AI-Gridded Result</span>
              </div>
            </div>
            <button 
              className="demo-button"
              onClick={handleDemoClick}
              disabled={state.isProcessing}
            >
              {state.isProcessing ? (
                <>
                  <div className="processing-spinner" />
                  <span>Generating Random Data...</span>
                </>
              ) : (
                'Demo or Upload your Data'
              )}
            </button>
          </div>
        </div>

        {/* Upload Section */}
        <div className="upload-section">
          <h3>Upload Your Own Data</h3>
          <div className="upload-instruction">
            <p>Upload a file containing:</p>
            <ul>
              <li>X/Longitude/Easting coordinates</li>
              <li>Y/Latitude/Northing coordinates</li>
              <li>Value data (any numeric column)</li>
            </ul>
            <p>Accepts: CSV, Excel, or text files (max 16MB)</p>
          </div>

          <div 
            className={`upload-area ${isDragging ? 'dragging' : ''}`}
            onDragEnter={() => setIsDragging(true)}
            onDragLeave={() => setIsDragging(false)}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileInputChange}
              accept=".csv,.xlsx,.xls,.txt"
              className="file-input"
            />
            {state.isProcessing ? (
              <div className="upload-status">
                <div className="processing-spinner" />
                <span>Processing data...</span>
              </div>
            ) : (
              <div className="upload-prompt">
                <svg className="upload-icon" viewBox="0 0 24 24">
                  <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z"/>
                </svg>
                <span>Choose a file or drag it here</span>
              </div>
            )}
          </div>

          {uploadError && (
            <div className="error-message">
              {uploadError}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

